import { computed, inject, Injectable, Signal } from '@angular/core';
import { IBusinessPartner, IFeatureConfig, IGlobalConfig, ILoanProfile, IPolicy, IProfileParams } from '@partners/api';
import { TCatalogOption } from '@partners/catalog';
import { filterResult, HttpService, tapResult } from '@partners/http';
import { TFeature } from '@partners/settings';
import { ShellStore } from '@partners/state';
import { excludeEmptyValues } from '@partners/utils';
import { lastValueFrom, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PolicyService {
  get globalConfig$$(): Signal<IGlobalConfig> {
    return this._shellStore.globalConfig$$;
  }

  get featuresConfig$$(): Signal<IFeatureConfig[]> {
    return this._shellStore.featuresConfig$$;
  }

  get policy$(): Observable<IPolicy> {
    return this._shellStore.policy$;
  }

  get policy$$(): Signal<IPolicy> {
    return this._shellStore.policy$$;
  }

  get businessPartner$$(): Signal<IBusinessPartner> {
    return this._shellStore.businessPartner$$;
  }

  get loanReasons$$(): Signal<TCatalogOption[]> {
    return this._shellStore.loanReasons$$;
  }

  get loanProfile$$(): Signal<ILoanProfile> {
    return this._shellStore.defaultLoanProfile$$;
  }

  get customerId$$(): Signal<string> {
    return computed(() => {
      return this._shellStore.policy$$().idNum;
    });
  }

  // Get indication whether the current process is post issue.
  get isPostCardIssue$$(): Signal<boolean> {
    return computed(() => {
      const issueConfig = this.findFeatureConfig$$('issue');
      return issueConfig()?.indication?.['isPostCardIssue'] ?? false;
    });
  }

  get isNewAccount$$(): Signal<boolean> {
    return computed(() => {
      const agreementConfig = this.findFeatureConfig$$('agreement-concent');
      if (!agreementConfig()?.indication) {
        return false;
      }
      return agreementConfig().indication['isNewAccount'];
    });
  }

  private readonly _shellStore = inject(ShellStore);
  private readonly _httpService = inject(HttpService);

  constructor() {}

  updateFeatureConfig(featureName: TFeature, featureConfig: IFeatureConfig = undefined): void {
    this._shellStore.dispatchUpdateFeatureConfig(featureName, featureConfig);
  }

  findFeatureConfig$$(featureName: TFeature): Signal<IFeatureConfig> {
    return this._shellStore.findFeatureConfig$$(featureName);
  }

  loadPolicy(params: IProfileParams = this._shellStore.params$$()): Promise<IPolicy> {
    this._shellStore.dispatchLoadPolicy();

    /** check if there is already policy repository in the store */
    const hasPolicyInStore = this._shellStore.hasPolicy$$();

    if (hasPolicyInStore) {
      return new Promise<IPolicy>((resolve) => {
        resolve(this._shellStore.policy$$());
      });
    }

    const policyParams: IProfileParams = excludeEmptyValues<IProfileParams>(params);

    const policy = this._httpService
      .fetch<{}, IPolicy>(`policy`, {
        params: { ...policyParams }
      })
      .pipe(
        catchError((error: any) => {
          this._shellStore.dispatchLoadPolicyFailure(error);
          return throwError(() => error);
        }),
        tapResult((policy: IPolicy) => {
          const { stages } = policy;
          // this._shellStore.dispatchLoadPolicySuccess(policy);
          this._shellStore.dispatchUpdateProcessProfile({ policy });
          this._shellStore.dispatchUpdateStages(stages);
        }),
        filterResult()
      );

    return lastValueFrom<IPolicy>(policy);
  }
}
